<template>
  <div>
    <div v-for="(serialTrackingData, idx) in serialTrackingDatas" :key="idx" class="tracking-record mb-6">
      <span class="font-medium h-11 my-4px flex items-center justify-between">
        {{ trackingPointName(serialTrackingData.operatorType) }}

        <el-button
          type="primary"
          size="medium"
          class="text-white focus:text-white"
          @click="openModal(serialTrackingData)"
        >
          {{ $t('barcode.link') }}
        </el-button>
      </span>
      <el-table
        class="rounded-xl w-full"
        :data="serialTrackingData.products"
        header-cell-class-name="custom-table__header-cell"
        cell-class-name="custom-table__cell"
        border
        :empty-text="$t('empty')"
        max-height="380px"
      >
        <el-table-column :label="$t('component_item_code')" prop="itemCode" :min-width="20" />
        <el-table-column :label="$t('component_item_name')" prop="itemName" :min-width="20" />
        <el-table-column :label="$t('barcode.plannedQuantityStockUnit')" :min-width="20">
          <template #default="{ row }">
            <span>{{ row.plannedQuantity }} {{ row.unit }}</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="serialTrackingData.operatorType === 'add' ? $t('initial_quantity') : $t('manufactured_quantity')"
          :min-width="20"
        >
          <template #default="{ row }">
            <span>{{ row.actualQuantity }} {{ row.unit }}</span>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-dialog v-model="dialogVisible" :title="dialogTitle" :show-close="false">
      <div>
        <div style="margin-bottom: 16px;">
          <table class="rounded-table">
            <thead>
              <tr class="text-center">
                <th v-for="(col, colIndex) in manufacturingOrderColumns" :key="colIndex">
                  {{ col.label }}
                </th>
              </tr>
            </thead>
            <tbody v-if="mo">
              <tr>
                <td v-for="(col, colIndex) in manufacturingOrderColumns" :key="colIndex">
                  <span v-if="col.formatter">{{ col.formatter(mo) }}</span>
                  <span v-else>{{ getNestedValue(mo, col.prop) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          style="margin-bottom: 16px; font-weight: bold; text-align: left; display: flex; justify-content: space-between; align-items: end;"
        >
          {{
            currentOperatorType === 'add'
              ? $t('barcode.serial_history_manufacturing')
              : $t('barcode.serial_history_usage')
          }}

          <el-button class="excel-export-btn" @click="exportCSV">{{ $t('barcode.excel_export') }}</el-button>
        </div>
        <div v-if="currentSerialTrackingData.length > 0" class="table-scroll-container">
          <table class="rounded-table sticky-header-table">
            <thead>
              <tr class="text-center">
                <th v-for="(col, colIndex) in productTableColumns" :key="colIndex" :colspan="col.colspan || 1">
                  {{ col.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in currentSerialTrackingData" :key="index">
                <template v-for="(col, colIndex) in productTableColumns" :key="colIndex">
                  <td
                    v-for="(cell, cellIndex) in col.cells"
                    :key="cellIndex"
                    :class="cell.className || ''"
                    @click="cell.onClick ? cell.onClick(item) : null"
                  >
                    <span v-if="cell.formatter">{{ cell.formatter(item) }}</span>
                    <span v-else-if="cell.value">{{ cell.value }}</span>
                    <span v-else>{{ item[cell.prop] }}</span>
                  </td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else style="text-align: center; padding: 20px; color: #909399;">
          {{ $t('empty') }}
        </div>
      </div>
      <template #footer>
        <el-button @click="dialogVisible = false" type="primary" class="flex justify-self-center">
          {{ $t('ok') }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { DATE_TIME, TIMETABLE_DATETIME } from '@/utils/constants'
import { saveCSVText } from '@/utils/exportPdf'
import { formatQuantityWithUnit, parseDateToYYMMDD } from '@/utils/helpers'
import { getNestedValue } from '@/utils/tableUtils'
import { formatDate } from '@/utils/timeUtils'
import { IBarcode, IProductData, ISerialData, ISerialTrackingData } from '@/utils/types'
import { IManufacturingPlan } from '@/utils/types/model/manufacturingPlan'
import { format } from 'date-fns'
import { Options, Vue } from 'vue-class-component'
import { Prop } from 'vue-property-decorator'

interface IManufacturingOrderColumn {
  prop: string
  label: string
  formatter?: (row: IManufacturingPlan) => string
}

@Options({
  name: 'SerialTrackingDatas',
})
export default class SerialTrackingDatas extends Vue {
  @Prop({ type: Array, default: () => [] }) serialTrackingDatas!: IProductData[]

  dialogVisible = false
  currentOperatorType = ''
  currentSerialTrackingData: ISerialData[] = []

  get mo(): IManufacturingPlan | null {
    return this.barcode?.manufacturingPlan || null
  }

  get dialogTitle(): string {
    if (this.currentOperatorType === 'add') {
      return '製造実績明細'
    } else if (this.currentOperatorType === 'use') {
      return '使用・残回収実績明細'
    }
    return ''
  }

  get productTableColumns() {
    if (this.currentOperatorType === 'add') {
      return [
        {
          label: this.$t('barcode.serialCode'),
          cells: [
            {
              prop: 'code',
              className: 'underline text-blue-500 cursor-pointer',
              onClick: (item: ISerialData) => this.goToSerialDetail(item.code),
            },
          ],
        },
        {
          label: this.$t('barcode.machineNo'),
          cells: [{ prop: 'machineCode' }],
        },
        {
          label: this.$t('barcode.trolley'),
          cells: [{ prop: 'trollyNumber' }],
        },
        {
          label: this.$t('barcode.manufacturing_serial_no'),
          cells: [{ prop: 'trollyIndex' }],
        },
        {
          label: this.$t('barcode.manufacturing_time'),
          cells: [
            {
              prop: 'scannedDateTime',
              formatter: (row: ISerialData) =>
                formatDate(row.scannedDateTime || '', TIMETABLE_DATETIME.TIME_WITH_SECOND_FORMAT),
            },
          ],
        },
        {
          label: this.$t('produced_amount'),
          cells: [
            {
              prop: 'currentAmount',
              formatter: (row: ISerialData) => (!row.amount ? '' : `${row.amount} ${row.unit ?? ''}`.trim()),
              exportFormatter: (row: ISerialData) => (!row.amount ? '' : `${row.amount}`),
              unitFormatter: (row: ISerialData) => (!row.amount ? '' : `${row.unit ?? ''}`),
            },
          ],
        },
        {
          label: this.$t('used_amount'),
          cells: [
            {
              prop: 'usageAmount',
              formatter: (row: ISerialData) => (!row.refAmount ? '' : `${row.refAmount} ${row.unit ?? ''}`.trim()),
              exportFormatter: (row: ISerialData) => (!row.refAmount ? '' : `${row.refAmount}`),
              unitFormatter: (row: ISerialData) => (!row.refAmount ? '' : `${row.unit ?? ''}`),
            },
          ],
        },
        {
          label: this.$t('barcode.usage_order_no'),
          cells: [{ prop: 'refOrderNumber' }],
        },
        {
          label: this.$t('barcode.usage_serial_no'),
          cells: [{ prop: 'refTrollyIndex' }],
        },
      ]
    } else if (this.currentOperatorType === 'use') {
      return [
        {
          label: this.$t('barcode.used_item'),
          cells: [{ prop: 'itemCode' }],
        },
        {
          label: this.$t('barcode.itemNameUsed'),
          cells: [{ prop: 'itemName' }],
        },
        {
          label: this.$t('manufacturing_order_no'),
          cells: [
            {
              prop: 'refOrderNumber',
              className: 'underline text-blue-500 cursor-pointer',
              onClick: (row: ISerialData) => {
                if (row.refOrderNumber) {
                  this.goToMODetail(row.refOrderNumber)
                }
              },
            },
          ],
        },
        {
          label: 'LOT',
          cells: [
            {
              prop: 'lot',
            },
          ],
        },
        {
          label: this.$t('barcode.serialCode'),
          cells: [
            {
              prop: 'code',
              className: 'underline text-blue-500 cursor-pointer',
              onClick: (item: ISerialData) => this.goToSerialDetail(item.code),
            },
          ],
        },
        {
          label: this.$t('barcode.machineNo'),
          cells: [{ prop: 'machineCode' }],
        },
        {
          label: this.$t('barcode.trolley'),
          cells: [{ prop: 'refTrollyNumber' }],
        },
        {
          label: this.$t('barcode.usage_serial_no'),
          cells: [{ prop: 'trollyIndex' }],
        },
        {
          label: this.$t('barcode.manufacturing_serial_no'),
          cells: [{ prop: 'refTrollyIndex' }],
        },
        {
          label: this.$t('barcode.usage_time'),
          cells: [
            {
              prop: 'scannedDateTime',
              formatter: (row: ISerialData) =>
                formatDate(row.scannedDateTime || '', TIMETABLE_DATETIME.TIME_WITH_SECOND_FORMAT),
            },
          ],
        },
        {
          label: this.$t('used_amount'),
          cells: [
            {
              prop: 'amount',
              formatter: (row: ISerialData) => (!row.amount ? '' : `${row.amount} ${row.unit ?? ''}`.trim()),
              exportFormatter: (row: ISerialData) => (!row.amount ? '' : `${row.amount}`),
              unitFormatter: (row: ISerialData) => (!row.amount ? '' : `${row.unit ?? ''}`),
            },
          ],
        },
      ]
    }

    return []
  }

  goToSerialDetail(id: string) {
    const route = this.$router.resolve({ name: 'serial-detail', params: { id } })
    window.open(route.href, '_blank')
  }

  goToMODetail(id: string | number) {
    const route = this.$router.resolve({ name: 'mo-detail', params: { id } })
    window.open(route.href, '_blank')
  }

  get manufacturingOrderColumns(): IManufacturingOrderColumn[] {
    return [
      { prop: 'orderNumber', label: this.$t('manufacturing_order_no') as string },
      {
        prop: 'manufactureCompletionDate',
        label: this.$t('manufacture_completion_date') as string,
        formatter: (row: IManufacturingPlan) =>
          formatDate(row.manufactureCompletionDate || '', TIMETABLE_DATETIME.DATE_DISPLAY_FORMAT),
      },
      {
        prop: 'workCenterCode',
        label: this.$t('barcode.workCenterCode') as string,
        formatter: (row: IManufacturingPlan) => `${row.workCenterCode} - ${row.workCenterName}`,
      },
      {
        prop: 'itemCode',
        label: this.$t('item_code') as string,
        formatter: (row: IManufacturingPlan) => `${row.itemCode} - ${row.itemName}`,
      },
      {
        prop: 'plannedQuantityStockUnit',
        label: this.$t('amount') as string,
        formatter: (row: IManufacturingPlan) => formatQuantityWithUnit(row.plannedQuantityStockUnit, row.stockUnit),
      },
      {
        prop: 'manufactureCompletionDate',
        label: this.$t('barcode.lot') as string,
        formatter: (row: IManufacturingPlan) => {
          if (!row.manufactureCompletionDate) return ''
          return parseDateToYYMMDD(row.manufactureCompletionDate)
        },
      },
      { prop: 'companyCode', label: this.$t('company_code') as string },
    ]
  }

  get exportingManufacturingOrderColumns(): IManufacturingOrderColumn[] {
    return [
      { prop: 'orderNumber', label: this.$t('barcode.orderNumber') as string },
      {
        prop: 'manufactureCompletionDate',
        label: this.$t('manufacturing_date') as string,
        formatter: (row: IManufacturingPlan) =>
          formatDate(row.manufactureCompletionDate || '', TIMETABLE_DATETIME.EXPORTING_MANUFACTURE_COMPLETION_DATE),
      },
      { prop: 'workCenterCode', label: this.$t('work_center_code') as string },
      { prop: 'workCenterName', label: this.$t('work_center_name') as string },
      { prop: 'itemCode', label: this.$t('barcode.manufacturedItemCode') as string },
      { prop: 'itemName', label: this.$t('barcode.manufactureItemName') as string },
      {
        prop: 'manufactureCompletionDate',
        label: 'LOT',
        formatter: (row: IManufacturingPlan) => {
          if (!row.manufactureCompletionDate) return ''
          return parseDateToYYMMDD(row.manufactureCompletionDate)
        },
      },
      { prop: 'plannedQuantityStockUnit', label: this.$t('manufacturing_quantity') as string },
      { prop: 'stockUnit', label: this.$t('unit') as string },
    ]
  }

  trackingPointName(name: string): string {
    switch (name) {
      case 'add':
        return this.$t('produced_amount')
      case 'use':
        return this.$t('used_amount')
      default:
        return name
    }
  }

  openModal(serialTrackingData: IProductData): void {
    if (!serialTrackingData.operatorType) return
    this.currentOperatorType = serialTrackingData.operatorType
    const trackingData = this.barcode?.serialHistoryDatas?.find(
      (data: ISerialTrackingData) => data.operatorType === this.currentOperatorType
    )
    this.currentSerialTrackingData = trackingData?.serials || []
    this.dialogVisible = true
  }

  get barcode(): IBarcode {
    return this.$store.state.barcode?.barcode
  }

  getNestedValue(obj: Record<string, unknown>, path: string): unknown {
    return getNestedValue(obj, path)
  }

  exportCSV(): void {
    if (this.currentSerialTrackingData.length === 0) {
      this.$message.warning(this.$t('empty') as string)
      return
    }

    let csvContent = '\uFEFF'

    if (this.mo) {
      const moHeaders = this.exportingManufacturingOrderColumns.map((col) => col.label)
      csvContent += moHeaders.join(',') + '\n'

      const moValues = this.exportingManufacturingOrderColumns.map((col) => {
        let value = ''
        if (col.formatter && this.mo) {
          value = col.formatter(this.mo)
        } else if (this.mo) {
          const rawValue = this.getNestedValue((this.mo as unknown) as Record<string, unknown>, col.prop)
          value = rawValue != null ? String(rawValue) : ''
        }
        if (value && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          value = `"${value.replace(/"/g, '""')}"`
        }
        return value
      })
      csvContent += moValues.join(',') + '\n'
    }

    const columns = this.productTableColumns
    const headers: string[] = []
    const dataKeys: Array<{ prop?: string; formatter?: (row: ISerialData) => string }> = []

    columns.forEach((col) => {
      col.cells.forEach(
        (cell: {
          prop?: string
          formatter?: (row: ISerialData) => string
          exportFormatter?: (row: ISerialData) => string
          unitFormatter?: (row: ISerialData) => string
        }) => {
          headers.push(col.label)
          dataKeys.push({
            prop: cell.prop,
            formatter: cell.exportFormatter || cell.formatter,
          })
          if (cell.unitFormatter) {
            headers.push(`${col.label} ${this.$t('unit')}`)
            dataKeys.push({ formatter: cell.unitFormatter })
          }
        }
      )
    })

    csvContent += headers.join(',') + '\n'

    this.currentSerialTrackingData.forEach((item) => {
      const row = dataKeys.map((key) => {
        let value = ''
        if (key.formatter) {
          value = key.formatter(item)
        } else if (key.prop) {
          value = item[key.prop as keyof ISerialData]?.toString() || ''
        }
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
          value = `"${value.replace(/"/g, '""')}"`
        }
        return value
      })
      csvContent += row.join(',') + '\n'
    })

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const fileName = `${this.currentOperatorType === 'add' ? 'manufacturing' : 'usage'}_${format(
      new Date(),
      DATE_TIME.CSV_FORMAT
    )}`
    saveCSVText(blob, fileName)
  }
}
</script>

<style lang="scss" scoped>
.table-scroll-container {
  max-height: 400px;
  overflow-y: auto;
  overflow-x: auto;
  border-radius: 12px;
  border: 0.5px solid #e0e0e0;
}

.rounded-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: none;
}

.sticky-header-table {
  display: table;
  width: 100%;
}

table thead th {
  background-color: #e0e0e0;
  color: #333333;
  font-weight: bold;
  padding: 20px 0;
  border: 0.5px solid #ebeef5;
  border-bottom: 0.5px solid #ddd;
  position: sticky;
  top: 0;
  z-index: 10;
}

td {
  border: 0.5px solid #ddd;
  padding: 8px;
  text-align: center;
  word-wrap: break-word;
  overflow-wrap: break-word;
  vertical-align: middle;
}

::v-deep .el-dialog {
  max-width: 1000px !important;
  width: 100% !important;
}
</style>
